import React from 'react';
import { connect } from 'react-redux';

import {
  workflowRemoveReviewApproval,
  workflowRemoveReviewApprovalCount,
  workflowSetReviewApprovalCount,
  workflowSetReviewApprovalDescription,
  workflowSetReviewApprovalOrder,
  workflowSetReviewApprovalTitle,
  workflowSetReviewPhaseApprovers,
  workflowToggleReviewApprovalType,
  workflowToggleReviewConditionRule,
} from '~/actions/workflow';
import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import { hasValidApprovalValue } from '~/components/Workflow/Workflow.utils';
import { WorkflowReviewFinalizeApprovalType } from '~/enums/WorkflowReviewFinalizeApprovalType';
import { useUsers } from '~/hooks/useUsers';
import { FormattedMessage, useIntl } from 'react-intl';
import { getReviewPhaseById } from '~/reducers/workflow';
import Box from '~/ui/components/Box/Box';
import EditableText from '~/ui/components/EditableText/EditableText';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import NumberInput from '~/ui/components/NumberInput/NumberInput';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';
import Text from '~/ui/components/Text/Text';
import { CHARACTER_INPUT_LIMIT_TEMPORARY } from '~/ui/enums/input';
import { useModal, useModalSortable } from '~/ui/hooks/modal';
import { toSnakeTrimmed } from '~/utils/strings';
import { coerceName } from '~/utils/workflow';

import ConditionRuleInput from '../../../shared/ConditionRuleInput';
import DescriptionTextAreaForm from '../../../shared/DescriptionTextAreaForm';
import WorkflowUserDepartmentMultiSelect from '../../../shared/WorkflowUserDepartmentMultiSelect';

const formSegmentNested = {
  mb: 5,
  backgroundColor: 'black-alpha-04',
  borderRadius: 'm',
};

function PhaseItem({
  phaseId,
  approval,
  index,
  canReorder,
  //connected
  disabled,
  workflow,
  workflowRemoveReviewApproval,
  workflowSetReviewApprovalTitle,
  workflowSetReviewApprovalDescription,
  workflowSetReviewPhaseApprovers,
  workflowToggleReviewConditionRule,
  workflowToggleReviewApprovalType,
  workflowSetReviewApprovalCount,
  workflowRemoveReviewApprovalCount,
  workflowSetReviewApprovalOrder,
}) {
  const intl = useIntl();
  const { id } = approval;
  const reviewApprovals =
    getReviewPhaseById(workflow, phaseId)?.approvals || [];
  const [sortableListModal, showSortableListModal] = useModalSortable({
    confirm: (sortedItems) =>
      workflowSetReviewApprovalOrder({
        orders: sortedItems.map((sortedItem) => sortedItem.value),
        phaseId,
      }),
    items: reviewApprovals.map((item, index) => ({
      value: item.id,
      label: coerceName(
        item.name,
        intl.formatMessage({
          id: 'PhaseItem.defaultReviewTaskName',
          defaultMessage: 'Review Task',
          description: 'Default name for a new review task.',
        }),
        index,
      ),
    })),
    text: intl.formatMessage({
      id: 'PhaseItem.sortText',
      defaultMessage: 'Sort',
      description: 'Sort text',
    }),
    title: intl.formatMessage({
      id: 'PhaseItem.reorderReviewsTitle',
      defaultMessage: 'Reorder Reviews',
      description: 'Title for reorder reviews modal',
    }),
  });

  const users = useUsers();

  const [deleteModal, showDeleteModal] = useModal({
    actionButton: {
      variant: 'red',
      text: intl.formatMessage({
        id: 'PhaseItem.removeButton',
        defaultMessage: 'Remove',
        description: 'Remove button text',
      }),
      errorHandler: () => {},
      promise: async () => await workflowRemoveReviewApproval({ id, phaseId }),
    },
    title: intl.formatMessage({
      id: 'PhaseItem.removeReviewTitle',
      defaultMessage: 'Remove Review?',
      description: 'Title for remove review modal',
    }),
    content: (
      <Text color="gray-900" variant="s-spaced">
        <FormattedMessage
          id="PhaseItem.removeReviewConfirmation"
          defaultMessage="Are you sure you want to remove the review? <strong>You won't be able to undo this action.</strong>"
          description="Confirmation message for removing a review"
          values={{
            strong: (chunks) => <Text variant="s-spaced-bold">{chunks}</Text>,
          }}
        />
      </Text>
    ),
  });

  const coercedTaskName = coerceName(
    approval.name,
    intl.formatMessage({
      id: 'PhaseItem.defaultReviewTaskName',
      defaultMessage: 'Review Task',
      description: 'Default name for a new review task.',
    }),
    index,
  );
  const taskName = toSnakeTrimmed(coercedTaskName);

  const getMenuItems = () => {
    const items = [];

    items.push({
      content: intl.formatMessage({
        id: 'PhaseItem.reorderReviewsMenuItem',
        defaultMessage: 'Reorder Reviews',
        description: 'Menu item for reordering reviews',
      }),
      disabled: !canReorder,
      onClick: () => showSortableListModal(),
    });

    items.push({
      content: intl.formatMessage({
        id: 'PhaseItem.deleteReviewMenuItem',
        defaultMessage: 'Delete Review',
        description: 'Menu item for deleting a review',
      }),
      onClick: () => showDeleteModal(),
    });

    return items;
  };

  const hasUserDepartmentError = !hasValidApprovalValue(approval, users);

  return (
    <Box>
      <Box sx={formSegmentNested}>
        <Box sx={{ borderBottom: 'border' }}>
          <FlexLayout p={4} alignItems="center" justifyContent="space-between">
            <EditableText
              id={`workflow--review-edit-task-name=${taskName}-name`}
              placeholder={intl.formatMessage(
                {
                  id: 'PhaseItem.taskPlaceholder',
                  defaultMessage: 'Review Task {index}',
                  description:
                    'Placeholder for review task name. Example: index = 1, 2, 3',
                },
                { index: index + 1 },
              )}
              value={approval.name}
              onChange={(value) =>
                workflowSetReviewApprovalTitle({ value, id, phaseId })
              }
            />
            <Box pl={4}>
              <ActionsMenu
                id={`workflow_builder_review_phase_menu_id=${phaseId}`}
                items={getMenuItems()}
                align="end"
              />
            </Box>
          </FlexLayout>
        </Box>
        <Box p={4}>
          <Box pb={2} sx={{ borderBottom: 'border' }}>
            <DescriptionTextAreaForm
              description={approval.description}
              disabled={disabled}
              id={`workflow--review-edit-task-name=${taskName}-description`}
              limit={CHARACTER_INPUT_LIMIT_TEMPORARY}
              onUpdate={(value) =>
                workflowSetReviewApprovalDescription({ value, id, phaseId })
              }
            />
          </Box>
          <Box pt={4} pb={4} mt={4} sx={{ borderBottom: 'border' }}>
            <ConditionRuleInput
              conditionRule={approval.conditionRule}
              id={`workflow--review-edit-task-name=${taskName}-condition-rule`}
              onChange={(conditionRule) =>
                workflowToggleReviewConditionRule({
                  conditionRule,
                  id,
                  phaseId,
                })
              }
            />
          </Box>
          <Box mt={4} mb={4}>
            <Text variant="xs-dense-caps">
              <FormattedMessage
                id="PhaseItem.reviewersLabel"
                defaultMessage="Reviewers"
                description="Label for reviewers section"
              />
            </Text>
          </Box>

          <Box mb={4}>
            <WorkflowUserDepartmentMultiSelect
              error={
                hasUserDepartmentError
                  ? intl.formatMessage({
                      id: 'PhaseItem.usersNotExistError',
                      defaultMessage:
                        "There are users that don't exist anymore",
                      description: 'Error message for non-existent users',
                    })
                  : ''
              }
              id={`workflow--review-edit-task-name=${taskName}-reviewers`}
              value={approval.approvers}
              onChange={(updatedApprovers) => {
                if (!updatedApprovers.length) {
                  workflowRemoveReviewApprovalCount({ id, phaseId });
                }
                workflowSetReviewPhaseApprovers({
                  approvers: updatedApprovers,
                  id,
                  phaseId,
                });
              }}
            />
          </Box>

          <Box mt={4}>
            <Text variant="xs-dense-caps">
              <FormattedMessage
                id="PhaseItem.assignToLabel"
                defaultMessage="Assign to"
                description="Label for assign to section"
              />
            </Text>
            <RadioGroup
              id={`workflow--review-edit-task-name=${taskName}-assign-to`}
              inline={true}
              label={intl.formatMessage({
                id: 'PhaseItem.requireApprovalFromLabel',
                defaultMessage: 'Require Approval From',
                description: 'Require Approval text',
              })}
              value={approval.approvalType}
              options={[
                {
                  label: intl.formatMessage({
                    id: 'PhaseItem.everyoneOption',
                    defaultMessage: 'Everyone',
                    description:
                      'Approve option for requiring approval from everyone',
                  }),
                  value: WorkflowReviewFinalizeApprovalType.Everyone,
                },
                {
                  label: intl.formatMessage({
                    id: 'PhaseItem.specificNumberOptionReviewers',
                    defaultMessage: 'Specific number of reviewers',
                    description:
                      'Option for requiring a specific number of reviewers',
                  }),
                  value: WorkflowReviewFinalizeApprovalType.Specific,
                },
              ]}
              onChange={(approvalType) =>
                workflowToggleReviewApprovalType({ approvalType, id, phaseId })
              }
            />
          </Box>

          {approval.approvalType !==
            WorkflowReviewFinalizeApprovalType.Everyone && (
            <FlexLayout alignItems="baseline" space={4} mt={4}>
              <Text
                color={!approval.approvers.length && 'gray-500'}
                variant="xs-dense"
              >
                <FormattedMessage
                  id="PhaseItem.numberOfReviewersText"
                  defaultMessage="Number of reviewers to assign this to"
                  description="Text for number of reviewers input"
                />
              </Text>
              <NumberInput
                disabled={!approval.approvers.length}
                id={`workflow--review-edit-task-name=${taskName}-number-reviewers`}
                value={approval.minApprovalCount}
                min={1}
                onChange={(minApprovalCount) =>
                  workflowSetReviewApprovalCount({
                    minApprovalCount,
                    id,
                    phaseId,
                  })
                }
              />
            </FlexLayout>
          )}
        </Box>
      </Box>
      {sortableListModal}
      {deleteModal}
    </Box>
  );
}

function mapStateToProps({ builder, workflow }) {
  return {
    disabled: !builder.shouldEnableAllComponents,
    workflow,
  };
}

export default connect(mapStateToProps, {
  workflowRemoveReviewApproval,
  workflowSetReviewApprovalTitle,
  workflowSetReviewApprovalDescription,
  workflowSetReviewPhaseApprovers,
  workflowToggleReviewConditionRule,
  workflowToggleReviewApprovalType,
  workflowSetReviewApprovalCount,
  workflowRemoveReviewApprovalCount,
  workflowSetReviewApprovalOrder,
})(PhaseItem);
